import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, FileText, BookOpen } from "lucide-react";
import { useContent } from "@/lib/ContentContext";
import SEO from "@/components/SEO";
import { useProducts } from "@/lib/ProductsContext";
import { EducationalDisclaimer } from "@/components/LegalNotices";

const HubDetailPage = ({ hubId }) => {
  const { guides: GUIDES, bundle: BUNDLE, freebies: FREEBIES } = useProducts();
  const { seoContent: SEO_CONTENT, articleHubs: ARTICLE_HUBS } = useContent();
  const hub = ARTICLE_HUBS[hubId];

  if (!hub) {
    return (
      <div className="section-padding container-main text-center">
        <h1>Hub not found</h1>
        <Link to="/glp-guide" className="btn-primary mt-4">Browse All Resources</Link>
      </div>
    );
  }

  const articles = hub.articles
    .map(slug => SEO_CONTENT[slug])
    .filter(Boolean);

  const relatedGuide = GUIDES[hub.relatedGuide];
  const relatedFreebie = FREEBIES[hub.relatedFreebie];

  return (
    <div className="noise-bg">
      <SEO title={hub?.title ? `${hub.title} — GLP-1 Articles` : "GLP-1 Articles"} description={hub?.description || "Educational articles for GLP-1 medication users."} />
      {/* Hero */}
      <section className="gradient-hero section-padding">
        <div className="container-main">
          <Link to="/glp-guide" className="inline-flex items-center gap-2 text-sm text-[#4B5548] hover:text-[#1A2018] mb-6">
            <ArrowLeft className="w-4 h-4" />
            Back to Resource Hub
          </Link>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl lg:text-7xl mb-6 leading-[1.02] tracking-tight" data-testid="hub-detail-title">{hub.title}</h1>
            <p className="text-lg text-[#4B5548]">{hub.description}</p>
          </motion.div>
        </div>
      </section>

      {/* Hub Intro */}
      <section className="section-padding bg-white">
        <div className="container-main">
          <div className="max-w-3xl">
            {hub.intro.split("\n\n").map((paragraph, i) => (
              <p key={i} className="text-lg text-[#4B5548] mb-6 leading-relaxed">{paragraph}</p>
            ))}
          </div>
        </div>
      </section>

      {/* Freebie CTA */}
      {relatedFreebie && (
        <section className="py-8 bg-[#E9EDC9]">
          <div className="container-main">
            <div className="max-w-2xl mx-auto text-center">
              <p className="text-lg font-semibold text-[#1A2018] mb-2">Start here for free</p>
              <p className="text-[#4B5548] mb-4">{relatedFreebie.description}</p>
              <Link to={`/free/${relatedFreebie.slug}`} className="btn-primary inline-flex" data-testid="hub-freebie-cta">
                Get the Free {relatedFreebie.title}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Articles Grid */}
      <section className="section-padding bg-[#F2F0E9]">
        <div className="container-main">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-full bg-[#E9EDC9] flex items-center justify-center">
              <FileText className="w-5 h-5 text-[#2D6A4F]" />
            </div>
            <h2 className="text-2xl">Articles in This Hub ({articles.length})</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {articles.map((article, index) => (
              <motion.div
                key={article.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <Link
                  to={`/articles/${article.slug}`}
                  className="card-elevated p-6 block h-full hover:border-[#2D6A4F] border-2 border-transparent transition-all"
                  data-testid={`hub-article-${article.slug}`}
                >
                  <h3 className="font-semibold text-[#1A2018] mb-3 leading-tight">{article.title}</h3>
                  <p className="text-sm text-[#4B5548] line-clamp-3">
                    {article.content.hook || article.content.intro}
                  </p>
                  <span className="mt-4 text-sm font-medium text-[#2D6A4F] flex items-center gap-1">
                    Read article <ArrowRight className="w-3 h-3" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Guide */}
      {relatedGuide && (
        <section className="section-padding bg-white">
          <div className="container-main">
            <div className="max-w-2xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="card-elevated p-8 border-2 border-[#2D6A4F]"
              >
                <div className="flex items-center gap-2 mb-4">
                  <BookOpen className="w-5 h-5 text-[#2D6A4F]" />
                  <span className="text-sm font-medium text-[#2D6A4F]">Recommended Guide</span>
                </div>
                <h3 className="text-xl font-semibold text-[#1A2018] mb-2">{relatedGuide.title}</h3>
                <p className="text-[#4B5548] mb-4">{relatedGuide.description}</p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link to={`/guides/${relatedGuide.slug}`} className="btn-secondary justify-center text-sm">
                    Learn More - ${relatedGuide.price}
                  </Link>
                  <Link to="/glp-survival-system" className="btn-primary justify-center text-sm">
                    Get All 8 Guides - ${BUNDLE?.price || 49}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      )}

      {/* Bundle CTA */}
      <section className="section-padding gradient-bundle text-white">
        <div className="container-main text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <span className="badge-best-value mb-4 inline-block">Save 60%+</span>
            <h2 className="text-white mb-4">Get the Complete GLP Survival System</h2>
            <p className="text-lg text-white/80 mb-6 max-w-2xl mx-auto">
              All 8 educational guides for just ${BUNDLE?.price || 49} (normally ${BUNDLE?.original_value || 127}). Manage side effects, build routines, and support your progress.
            </p>
            <Link to="/glp-survival-system" className="btn-coral inline-flex" data-testid="hub-bundle-cta">
              Get the Complete System <ArrowRight className="w-5 h-5" />
            </Link>
            <EducationalDisclaimer className="mt-6 text-white/60" />
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HubDetailPage;
